import { useState, useEffect } from 'react';
import { getMatchWithDetails, calculateDuration, getTeams, lockMatch, unlockMatch } from '@/lib/supabase';
import { Team, SetRecord, Rally } from '@/types';
import { TeamLogo } from '@/components/TeamLogo';
import RallyHistoryModal from './RallyHistoryModal';
import { getSetScoreColor } from '@/lib/setScoreColor';

interface MatchDetailsDrawerProps {
  matchId: string;
  tournamentName?: string;
  onClose: () => void;
  onEdit?: () => void;
  onDiscard?: () => void;
  onStart?: () => void;
  onResume?: () => void;
  onView?: () => void;
  isAdmin: boolean;
  isScorer: boolean;
  isViewer: boolean;
  onLockChange?: () => void;
}

interface MatchDetails {
  id: string;
  match_number: number;
  date: string;
  match_time: string | null;
  match_type: 'league' | 'playoff';
  status: string;
  team_a_id: string;
  team_b_id: string;
  winner_id: string | null;
  started_at: string | null;
  ended_at: string | null;
  is_locked: boolean;
  sets: (SetRecord & { rallies?: Rally[] })[];
}

export default function MatchDetailsDrawer({
  matchId,
  tournamentName,
  onClose,
  onEdit,
  onDiscard,
  onStart,
  onResume,
  onView,
  isAdmin,
  isScorer,
  isViewer,
  onLockChange,
}: MatchDetailsDrawerProps) {
  const [match, setMatch] = useState<MatchDetails | null>(null);
  const [teams, setTeams] = useState<Map<string, Team>>(new Map());
  const [loading, setLoading] = useState(true);
  const [lockToggling, setLockToggling] = useState(false);
  const [historyModal, setHistoryModal] = useState<{ setNumber: number; setRecord: SetRecord; rallies: Rally[] } | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [matchData, teamsData] = await Promise.all([
          getMatchWithDetails(matchId),
          getTeams(),
        ]);
        setMatch(matchData as MatchDetails);
        const teamMap = new Map<string, Team>();
        teamsData.forEach((t: any) => teamMap.set(t.id, {
          id: t.id,
          name: t.name,
          shortName: t.short_name,
          color: t.color,
          logoUrl: t.logo_url,
          players: (t.players || []).map((p: any) => ({
            id: p.id,
            name: p.name,
            number: p.number,
            teamId: p.team_id,
          })),
        }));
        setTeams(teamMap);
      } catch (err) {
        console.error('Failed to load match details:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [matchId]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!match) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
        <div className="bg-surface rounded-xl p-6 max-w-sm w-full text-center">
          <p className="text-text-secondary">Match not found</p>
          <button onClick={onClose} className="btn-secondary mt-4">Close</button>
        </div>
      </div>
    );
  }

  const teamA = teams.get(match.team_a_id);
  const teamB = teams.get(match.team_b_id);
  const winner = match.winner_id ? teams.get(match.winner_id) : null;
  const duration = calculateDuration(match.started_at, match.ended_at);

  const isScheduled = match.status === 'scheduled';
  const isLive = match.status === 'live' || match.status === 'set-won' || match.status === 'first-server';
  const isCompleted = match.status === 'match-won';
  const isLocked = match.is_locked;

  const handleToggleLock = async () => {
    if (!match) return;
    setLockToggling(true);
    try {
      if (isLocked) {
        await unlockMatch(match.id);
      } else {
        await lockMatch(match.id);
      }
      // Reload match data
      const matchData = await getMatchWithDetails(matchId);
      setMatch(matchData as MatchDetails);
      onLockChange?.();
    } catch (err) {
      console.error('Failed to toggle lock:', err);
    } finally {
      setLockToggling(false);
    }
  };

  const formatDateTime = (date: string, time: string | null) => {
    const dateStr = new Date(date).toLocaleDateString();
    return time ? `${dateStr} at ${time}` : dateStr;
  };

  const formatSetDuration = (startedAt: string | null, endedAt: string | null): string | null => {
    return calculateDuration(startedAt, endedAt);
  };

  const getStatusBadge = () => {
    switch (match.status) {
      case 'match-won':
        return <span className="px-3 py-1 text-xs font-bold rounded-full bg-success/20 text-success">COMPLETED</span>;
      case 'live':
      case 'set-won':
        return <span className="px-3 py-1 text-xs font-bold rounded-full bg-warning/20 text-warning animate-pulse">LIVE</span>;
      case 'scheduled':
        return <span className="px-3 py-1 text-xs font-bold rounded-full bg-info/20 text-info">SCHEDULED</span>;
      case 'first-server':
        return <span className="px-3 py-1 text-xs font-bold rounded-full bg-warning/20 text-warning">READY TO START</span>;
      default:
        return <span className="px-3 py-1 text-xs font-bold rounded-full bg-surface-raised text-text-muted">PENDING</span>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex flex-col">
      <div className="bg-surface flex-1 overflow-y-auto max-w-lg mx-auto w-full">
        {/* Header */}
        <div className="sticky top-0 bg-surface border-b border-border p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold text-text-primary">Match #{match.match_number}</h2>
            {tournamentName && <p className="text-xs text-text-muted">{tournamentName}</p>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface-raised rounded-lg transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Status, Type & Lock */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              {getStatusBadge()}
              {isCompleted && isLocked && (
                <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded bg-surface-raised text-text-muted">
                  <span>🔒</span> Locked
                </span>
              )}
              {isCompleted && !isLocked && (
                <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded bg-warning/10 text-warning">
                  <span>🔓</span> Unlocked
                </span>
              )}
            </div>
            <span className="text-xs font-semibold text-text-muted uppercase">{match.match_type}</span>
          </div>

          {/* Admin Lock/Unlock Button */}
          {isAdmin && isCompleted && (
            <button
              onClick={handleToggleLock}
              disabled={lockToggling}
              className={`w-full py-2 px-4 rounded-lg font-bold text-sm flex items-center justify-center gap-2 ${
                isLocked
                  ? 'bg-warning/10 text-warning border border-warning/30 hover:bg-warning/20'
                  : 'bg-surface-raised text-text-muted border border-border hover:bg-border'
              } disabled:opacity-50`}
            >
              {lockToggling ? (
                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : isLocked ? (
                <>
                  <span>🔓</span>
                  UNLOCK MATCH
                </>
              ) : (
                <>
                  <span>🔒</span>
                  LOCK MATCH
                </>
              )}
            </button>
          )}

          {/* Teams */}
          <div className="card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {teamA && (
                  <div className="flex items-center gap-2">
                    <TeamLogo team={teamA} size="sm" />
                    <span className="font-semibold" style={{ color: teamA.color }}>{teamA.shortName}</span>
                  </div>
                )}
              </div>
              <span className="text-text-muted font-bold">VS</span>
              <div className="flex items-center gap-3">
                {teamB && (
                  <div className="flex items-center gap-2">
                    <span className="font-semibold" style={{ color: teamB.color }}>{teamB.shortName}</span>
                    <TeamLogo team={teamB} size="sm" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Schedule Info */}
          <div className="card bg-surface-raised">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Schedule</h3>
            <p className="text-sm text-text-primary">{formatDateTime(match.date, match.match_time)}</p>
          </div>

          {/* Winner & Duration (Completed matches) */}
          {isCompleted && (
            <>
              {winner && (
                <div className="card bg-success/10 border-success/30">
                  <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Winner</h3>
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full" style={{ backgroundColor: winner.color }} />
                    <span className="font-bold text-success">{winner.name}</span>
                  </div>
                </div>
              )}
              {duration && (
                <div className="card bg-surface-raised">
                  <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Match Duration</h3>
                  <p className="text-lg font-bold text-text-primary">{duration}</p>
                </div>
              )}
            </>
          )}

          {/* Set Scores */}
          {match.sets && match.sets.length > 0 && (
            <div className="card">
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Set Scores</h3>
              <div className="space-y-2">
                {match.sets.map((set) => {
                  const s = set as any;
                  const setDuration = formatSetDuration(s.startedAt ?? s.started_at, s.endedAt ?? s.ended_at);
                  const { color: scoreColor } = getSetScoreColor(set, teamA, teamB);

                  return (
                    <div key={set.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <span className="text-xs font-semibold text-text-muted w-16">Set {s.setNumber ?? s.set_number}</span>
                      <div className="flex items-center gap-3 flex-1 justify-center">
                        {teamA && (
                          <span className="font-mono text-lg font-bold" style={{ color: scoreColor ?? undefined }}>
                            {s.scoreA ?? s.score_a}
                          </span>
                        )}
                        <span className="text-text-muted">-</span>
                        {teamB && (
                          <span className="font-mono text-lg font-bold" style={{ color: scoreColor ?? undefined }}>
                            {s.scoreB ?? s.score_b}
                          </span>
                        )}
                      </div>
                      <div className="w-24 text-right">
                        {setDuration && (
                          <span className="text-xs text-text-muted">{setDuration}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Not Started (Scheduled) */}
          {isScheduled && (
            <div className="card bg-surface-raised text-center py-6">
              <p className="text-text-muted text-sm">Match not started yet</p>
            </div>
          )}

          {/* Rally History Button (for completed/live matches) */}
          {(isCompleted || isLive) && match.sets && match.sets.length > 0 && (
            <div className="card">
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Rally History</h3>
              <div className="space-y-2">
                {match.sets.map((set) => {
                  const s = set as any;
                  const setNum = s.setNumber ?? s.set_number;
                  return (
                    <button
                      key={set.id}
                      onClick={() => setHistoryModal({ setNumber: setNum, setRecord: set, rallies: set.rallies || [] })}
                      className="w-full flex items-center justify-between py-2 px-3 rounded-lg bg-surface-raised hover:bg-border transition-colors text-left"
                    >
                      <span className="text-sm font-medium text-text-primary">Set {setNum}</span>
                      <span className="text-xs text-text-muted">{set.rallies?.length || 0} rallies</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-4 space-y-2">
            {isScheduled && !isViewer && (isAdmin || isScorer) && onStart && (
              <button onClick={onStart} className="btn-primary bg-success text-white w-full">
                START MATCH
              </button>
            )}
            {isLive && !isViewer && (isAdmin || isScorer) && onResume && (
              <button onClick={onResume} className="btn-primary bg-warning text-white w-full">
                RESUME MATCH
              </button>
            )}
            {isLive && isViewer && onView && (
              <button onClick={onView} className="btn-primary bg-team-a text-white w-full">
                VIEW MATCH
              </button>
            )}
            {isCompleted && (
              <button onClick={isViewer ? onView : onResume} className="btn-secondary w-full">
                VIEW MATCH
              </button>
            )}
            {isScheduled && isAdmin && onEdit && (
              <button onClick={onEdit} className="btn-secondary w-full">
                EDIT MATCH
              </button>
            )}
            {(isScheduled || isLive) && isAdmin && onDiscard && (
              <button onClick={onDiscard} className="btn-secondary bg-error/10 text-error border-error/30 w-full hover:bg-error/20">
                DISCARD MATCH
              </button>
            )}
            <button onClick={onClose} className="btn-secondary w-full">
              CLOSE
            </button>
          </div>
        </div>
      </div>

      {/* Rally History Modal */}
      {historyModal && teamA && teamB && (
        <RallyHistoryModal
          setNumber={historyModal.setNumber}
          setRecord={historyModal.setRecord}
          rallies={historyModal.rallies}
          teamA={teamA}
          teamB={teamB}
          onClose={() => setHistoryModal(null)}
        />
      )}
    </div>
  );
}
